import "./EditDocument.scss";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  FormControlLabel,
  Grid,
  Link,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Breadcrumb } from "../../../breadcrumb/components/Breadcrumb";
import { OPTIVAL_CONSTANTS } from "../../../../shared/Constants";
import { useAPI } from "../../../../shared/services/api/API";
import {
  DOCUMENT_CATEGORY_LABELS,
  MAX_DOCUMENT_SUMMARY_LENGTH,
  type DocumentCategory,
} from "../../constants/documentManagementConstants";
import {
  buildMetadataPatchBody,
  buildRoleAccessPatchBody,
  mapDocumentListItem,
  RAG_API,
  type RagDocumentListResponse,
} from "../../services/ragDocumentApi";
import {
  extractApiErrorMessage,
  springErrorMessage,
} from "../../services/apiErrorUtils";
import type {
  DocumentMetadataForm,
  DocumentRowUi,
  RoleOptionUi,
} from "../../types/documentManagementUiTypes";
import {
  metadataFieldLabel,
  normalizeRoleAccessForSave,
  parseRoleAccess,
} from "../../utils/documentManagementUiUtils";
import { DocumentRoleAccessSelect } from "../shared/DocumentRoleAccessSelect";

function EditFormField({
  label,
  md = 4,
  children,
}: {
  label: React.ReactNode;
  md?: number;
  children: React.ReactNode;
}) {
  return (
    <Grid item xs={12} sm={12} md={md} lg={md}>
      <Typography variant="bodySmall">{label}</Typography>
      <div style={{ display: "flex", flexDirection: "row" }}>{children}</div>
    </Grid>
  );
}

type LocationState = {
  document?: DocumentRowUi;
  returnSubTab?: number;
};

export const EditDocument = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { httpGet, httpPut, handleAlertBar } = useAPI();

  const queryParams = new URLSearchParams(location.search);
  const documentIdParam = queryParams.get("documentId");
  const documentId = documentIdParam ? parseInt(documentIdParam, 10) : NaN;

  const state = (location.state as LocationState | null) ?? {};
  const returnSubTab = state.returnSubTab ?? 1;

  const [document, setDocument] = useState<DocumentRowUi | null>(
    state.document ?? null
  );
  const [loading, setLoading] = useState(!state.document && !Number.isNaN(documentId));
  const [loadError, setLoadError] = useState("");
  const [roleOptions, setRoleOptions] = useState<RoleOptionUi[]>([]);
  const [metadataForm, setMetadataForm] = useState<DocumentMetadataForm>({
    company_name: "",
    product_name: "",
    model_name: "",
    summary: "",
    document_category: "",
  });
  const [roleAccess, setRoleAccess] = useState<string[]>(["*"]);
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [disableConfirmOpen, setDisableConfirmOpen] = useState(false);

  const applyDocument = useCallback((row: DocumentRowUi) => {
    setDocument(row);
    setMetadataForm({
      company_name: row.company_name || "",
      product_name: row.product_name || "",
      model_name: row.model_name || "",
      summary: row.summary || "",
      document_category: row.document_category || "",
    });
    setRoleAccess(parseRoleAccess(row.role_access));
    setIsActive(row.is_active);
  }, []);

  useEffect(() => {
    if (state.document) {
      applyDocument(state.document);
      setLoading(false);
      return;
    }
    if (Number.isNaN(documentId)) {
      setLoadError(
        t("opva.dmEditMissingId", {
          defaultValue: "No document selected.",
        })
      );
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError("");

    httpGet(RAG_API.documents, undefined, false)
      .then((response) => {
        if (cancelled) return;
        if (response.status >= 400) {
          setLoadError(
            springErrorMessage(
              response.data,
              response.status,
              t("opva.dmDocumentsLoadFailed", {
                defaultValue: "Could not load documents.",
              })
            )
          );
          return;
        }
        const body = response.data as RagDocumentListResponse;
        const found = body?.items?.find((item) => item.id === documentId);
        if (!found) {
          setLoadError(
            t("opva.dmDocumentNotFound", {
              defaultValue: "Document not found on the server.",
            })
          );
          return;
        }
        applyDocument(mapDocumentListItem(found));
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(
            extractApiErrorMessage(
              err,
              t("opva.dmDocumentsLoadFailed", {
                defaultValue: "Could not load documents.",
              })
            )
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- httpGet unstable
  }, [documentId, state.document, applyDocument, t]);

  useEffect(() => {
    httpGet(OPTIVAL_CONSTANTS.API_URLS.GET_ROLES, undefined, false)
      .then((response) => {
        if (!response?.data || response.data?.error) return;
        const raw = response.data as Array<{
          applicationRole?: boolean;
          id: number;
          name: string;
        }>;
        const roles = raw
          .filter((r) => !r.applicationRole)
          .map((r) => ({ id: r.id, name: r.name }));
        if (roles.length) setRoleOptions(roles);
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialRoleAccess = useMemo(
    () =>
      normalizeRoleAccessForSave(
        document ? parseRoleAccess(document.role_access) : ["*"],
        roleOptions
      ),
    [document, roleOptions]
  );

  const initialMetadata = useMemo(
    (): DocumentMetadataForm | null =>
      document
        ? {
            company_name: document.company_name || "",
            product_name: document.product_name || "",
            model_name: document.model_name || "",
            summary: document.summary || "",
            document_category: document.document_category || "",
          }
        : null,
    [document]
  );

  const metadataDirty =
    !!initialMetadata &&
    (metadataForm.company_name !== initialMetadata.company_name ||
      metadataForm.product_name !== initialMetadata.product_name ||
      metadataForm.model_name !== initialMetadata.model_name ||
      metadataForm.summary !== initialMetadata.summary);

  const normalizedRoleAccess = useMemo(
    () => normalizeRoleAccessForSave(roleAccess, roleOptions),
    [roleAccess, roleOptions]
  );

  const rolesDirty =
    JSON.stringify([...normalizedRoleAccess].sort()) !==
    JSON.stringify([...initialRoleAccess].sort());

  const activeDirty = document != null && isActive !== document.is_active;

  const isDirty = metadataDirty || rolesDirty || activeDirty;

  const summaryLength = metadataForm.summary.length;
  const summaryTooLong = summaryLength > MAX_DOCUMENT_SUMMARY_LENGTH;
  const summaryCharCountLabel = t("opva.dmSummaryCharCount", {
    defaultValue: "{{current}}/{{max}}",
    current: summaryLength,
    max: MAX_DOCUMENT_SUMMARY_LENGTH,
  });
  const summaryTooLongMessage = t("opva.dmSummaryTooLong", {
    defaultValue: "Summary must be at most {{max}} characters.",
    max: MAX_DOCUMENT_SUMMARY_LENGTH,
  });

  const backToLibrary = () => {
    navigate("/document-management", {
      state: { dmDocumentsSubTab: returnSubTab },
    });
  };

  const patchLifecycle = async (active: boolean): Promise<boolean> => {
    if (!document) return false;
    const url = active
      ? RAG_API.documentEnable(document.id)
      : RAG_API.documentDisable(document.id);
    const response = await httpPut(url, {}, undefined, false);
    if (response.status === 403) {
      handleAlertBar(
        "error",
        t("opva.dmForbidden", {
          defaultValue: "Org Admin role is required to manage documents.",
        })
      );
      return false;
    }
    if (response.status >= 400) {
      handleAlertBar(
        "error",
        springErrorMessage(
          response.data,
          response.status,
          active
            ? t("opva.dmEnableFailed", {
                defaultValue: "Could not enable the document.",
              })
            : t("opva.dmDisableFailed", {
                defaultValue: "Could not disable the document.",
              })
        )
      );
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!document || !isDirty) return;
    if (summaryTooLong) {
      handleAlertBar("error", summaryTooLongMessage);
      return;
    }
    setSaving(true);
    try {
      if (metadataDirty) {
        const metaRes = await httpPut(
          RAG_API.documentMetadata(document.id),
          buildMetadataPatchBody(metadataForm),
          undefined,
          false
        );
        if (metaRes.status === 403) {
          handleAlertBar(
            "error",
            t("opva.dmForbidden", {
              defaultValue: "Org Admin role is required to manage documents.",
            })
          );
          return;
        }
        if (metaRes.status >= 400) {
          handleAlertBar(
            "error",
            springErrorMessage(
              metaRes.data,
              metaRes.status,
              t("opva.dmMetadataUpdateFailed", {
                defaultValue: "Could not update document metadata.",
              })
            )
          );
          return;
        }
      }

      if (rolesDirty) {
        if (!normalizedRoleAccess.length) {
          handleAlertBar(
            "error",
            t("opva.dmRoleRequired", {
              defaultValue: "Select at least one role or choose all roles.",
            })
          );
          return;
        }
        const roleRes = await httpPut(
          RAG_API.documentRoleAccess(document.id),
          buildRoleAccessPatchBody(normalizedRoleAccess),
          undefined,
          false
        );
        if (roleRes.status === 403) {
          handleAlertBar(
            "error",
            t("opva.dmForbidden", {
              defaultValue: "Org Admin role is required to manage documents.",
            })
          );
          return;
        }
        if (roleRes.status >= 400) {
          handleAlertBar(
            "error",
            springErrorMessage(
              roleRes.data,
              roleRes.status,
              t("opva.dmRoleUpdateFailed", {
                defaultValue: "Could not update document roles.",
              })
            )
          );
          return;
        }
      }

      if (activeDirty) {
        const ok = await patchLifecycle(isActive);
        if (!ok) return;
      }

      const roleJson = JSON.stringify(normalizedRoleAccess);
      setDocument((prev) =>
        prev
          ? {
              ...prev,
              ...metadataForm,
              role_access: roleJson,
              is_active: isActive,
            }
          : null
      );

      handleAlertBar(
        "success",
        t("opva.dmDocumentUpdated", {
          defaultValue: "Document updated successfully.",
        })
      );
      backToLibrary();
    } catch (err) {
      handleAlertBar(
        "error",
        extractApiErrorMessage(
          err,
          t("opva.dmDocumentUpdateFailed", {
            defaultValue: "Could not save document changes.",
          })
        )
      );
    } finally {
      setSaving(false);
      setDisableConfirmOpen(false);
    }
  };

  const handleActiveToggle = (checked: boolean) => {
    if (!document) return;
    if (!checked && document.is_active) {
      setDisableConfirmOpen(true);
      setIsActive(false);
      return;
    }
    setIsActive(checked);
    setDisableConfirmOpen(false);
  };

  const categoryKey = metadataForm.document_category as DocumentCategory;
  const categoryLabel =
    categoryKey in DOCUMENT_CATEGORY_LABELS
      ? DOCUMENT_CATEGORY_LABELS[categoryKey]
      : metadataForm.document_category || "—";

  const updateDocumentLabel = t("opva.dmUpdateDocument", {
    defaultValue: "Update document",
  });

  const pageHeader = (
    <>
      <Breadcrumb
        pathList={[
          { name: t("opva.management"), path: "management", isLink: true },
          {
            name: t("opva.documentManagement"),
            path: "document-management",
            isLink: true,
          },
          { name: updateDocumentLabel, path: "edit-document" },
        ]}
      />
      <Grid container alignItems="center" className="pt-2 pb-4">
        <Grid item xs md lg={6} className="d-flex align-items-center">
          <Link
            component="button"
            type="button"
            title={t("opva.dmBackToDocuments", {
              defaultValue: "Back to documents",
            })}
            className="me-2"
            onClick={backToLibrary}
          >
            <KeyboardArrowLeftIcon fontSize="large" />
          </Link>
          <Typography variant="h3" className="mb-0">
            {updateDocumentLabel}
          </Typography>
        </Grid>
      </Grid>
    </>
  );

  if (loading) {
    return (
      <div style={{ overflow: "hidden" }}>
        <div className="user-management px-5 pt-2 mb-3">{pageHeader}</div>
        <div className="user-management-content px-4">
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, m: 4 }}>
            {t("opva.loading", { defaultValue: "Loading…" })}
          </Typography>
        </div>
      </div>
    );
  }

  if (loadError || !document) {
    return (
      <div style={{ overflow: "hidden" }}>
        <div className="user-management px-5 pt-2 mb-3">{pageHeader}</div>
        <div className="user-management-content px-4">
          <Grid container>
            <Grid item xs={12} className="m-4">
            <Typography color="error" sx={{ mb: 2 }}>
              {loadError ||
                t("opva.dmDocumentNotFound", {
                  defaultValue: "Document not found on the server.",
                })}
            </Typography>
            <Button
              size="large"
              variant="contained"
              sx={{ color: "#FFFFFFCC", textTransform: "none" }}
              onClick={backToLibrary}
            >
              {t("opva.dmBackToDocuments", {
                defaultValue: "Back to documents",
              })}
            </Button>
            </Grid>
          </Grid>
        </div>
      </div>
    );
  }

  return (
    <div style={{ overflow: "hidden" }}>
      <div className="user-management px-5 pt-2 mb-3">{pageHeader}</div>
      <div className="user-management-content px-4">
        <Grid container>
          <Grid item xs={12} sm={12} md={7} lg={7} className="m-4">
            <form onSubmit={(e) => e.preventDefault()} noValidate>
              <Grid item xs={12} className="mb-4">
                <Typography variant="bodyMedium" className="fw-bold mb-2">
                  {updateDocumentLabel}
                </Typography>
              </Grid>

              <Grid container spacing={2} className="mb-4">
                <EditFormField
                  label={t("opva.dmColDocument", { defaultValue: "Document" })}
                  md={12}
                >
                  <TextField
                    fullWidth
                    variant="standard"
                    value={document.original_file_name}
                    disabled
                  />
                </EditFormField>
                <EditFormField
                  label={t("opva.dmColDataset", { defaultValue: "Dataset" })}
                >
                  <TextField
                    fullWidth
                    variant="standard"
                    value={document.dataset_name || "—"}
                    disabled
                  />
                </EditFormField>
                <EditFormField
                  label={t("opva.dmColStatus", { defaultValue: "Status" })}
                >
                  <TextField
                    fullWidth
                    variant="standard"
                    value={document.upload_status || "—"}
                    disabled
                  />
                </EditFormField>
                <EditFormField
                  label={t("opva.dmDetailCategory", { defaultValue: "Category" })}
                >
                  <TextField
                    fullWidth
                    variant="standard"
                    value={categoryLabel}
                    disabled
                  />
                </EditFormField>
                <EditFormField
                  label={t("opva.dmActiveStatus", {
                    defaultValue: "Active status",
                  })}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isActive}
                        onChange={(e) => handleActiveToggle(e.target.checked)}
                        disabled={saving}
                      />
                    }
                    label={
                      isActive
                        ? t("opva.dmActiveLabel", { defaultValue: "Active" })
                        : t("opva.dmDisabledLabel", { defaultValue: "Inactive" })
                    }
                  />
                </EditFormField>
              </Grid>

              {disableConfirmOpen ? (
                <Grid item xs={12} className="mb-4">
                  <Typography variant="bodySmall" color="text.secondary">
                    {t("opva.dmDisableOnSaveHint", {
                      defaultValue:
                        "Document will be disabled when you save (hidden from retrieval).",
                    })}
                  </Typography>
                </Grid>
              ) : null}

              <Grid container spacing={2} className="mb-4">
                <EditFormField
                  label={
                    <>
                      {t("opva.dmColRoles", { defaultValue: "Roles" })}
                      <span className="asterisk">*</span>
                    </>
                  }
                  md={12}
                >
                  <DocumentRoleAccessSelect
                    roleAccessRaw={JSON.stringify(roleAccess)}
                    roleOptions={roleOptions}
                    disabled={saving || !roleOptions.length}
                    onChange={setRoleAccess}
                    variant="standard"
                  />
                </EditFormField>
              </Grid>

              <Grid container spacing={2} className="mb-4">
                {(["company_name", "product_name", "model_name"] as const).map(
                  (key) => (
                    <EditFormField key={key} label={metadataFieldLabel(key, t)}>
                      <TextField
                        fullWidth
                        variant="standard"
                        value={metadataForm[key]}
                        onChange={(e) =>
                          setMetadataForm((f) => ({
                            ...f,
                            [key]: e.target.value,
                          }))
                        }
                        disabled={saving}
                      />
                    </EditFormField>
                  )
                )}
                <EditFormField
                  label={t("opva.dmSummaryField", { defaultValue: "Summary" })}
                  md={12}
                >
                  <TextField
                    fullWidth
                    variant="standard"
                    value={metadataForm.summary}
                    onChange={(e) =>
                      setMetadataForm((f) => ({
                        ...f,
                        summary: e.target.value.slice(0, MAX_DOCUMENT_SUMMARY_LENGTH),
                      }))
                    }
                    multiline
                    minRows={3}
                    disabled={saving}
                    inputProps={{ maxLength: MAX_DOCUMENT_SUMMARY_LENGTH }}
                    error={summaryTooLong}
                    helperText={
                      summaryTooLong ? summaryTooLongMessage : summaryCharCountLabel
                    }
                  />
                </EditFormField>
              </Grid>

              <Grid container spacing={2} className="mb-4">
                <Grid item xs={12} sm={12} md={4} lg={4}>
                  <Button
                    size="large"
                    variant="contained"
                    sx={{ color: "#FFFFFFCC", textTransform: "none" }}
                    onClick={() => void handleSave()}
                    disabled={saving || !isDirty || summaryTooLong}
                  >
                    {updateDocumentLabel}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Grid>
        </Grid>
      </div>
    </div>
  );
};
